import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Row,
  Select,
  Input,
  Space,
  Drawer,
  Table,
  TableColumnType,
  InputRef,
  TableColumnsType,
  Tooltip,
  Spin,
} from "antd";
import useFetch from "../../../../hooks/useFetch";
import CustomModal from "../../../../components/modal";
import useToast from "../../../../hooks/useToast";
import { TDealer } from "../../../../types";
import {
  AddEditDealerPartnerMapping,
  GetCustMappedPartnerList,
  GetCustomerList,
  GetDealerList,
  GetDealerPartnerMappingList,
  GetStateList,
} from "../../../../utils/api.constant";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  TDropdownOptions,
  TFilterModel,
  TResponseModel,
  TUser,
} from "../../../../types/config";
import {
  eDEALER_VIEW_TYPE,
  eMAPPED_DEALER_VIEW,
  ePrivileges,
  eResultCode,
} from "../../../../utils/enum";
import Utils from "../../../../utils";
import { UserContext } from "../../../../context";
import { STAKE_HOLDERS } from "../../../../utils/constants";
import {
  CheckOutlined,
  CloseOutlined,
  FilterFilled,
  FilterOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../../components/configurable-columns";
import { PageHeader } from "../../../../components/page-header/page-header";
import styles from "./dealer.module.css";
import Highlighter from "react-highlight-words";
import {
  FilterDropdownProps,
  TablePaginationConfig,
} from "antd/es/table/interface";
import ExportToCSV from "../../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../../utils/date";
import MappingForm from "./eng-mappingForm";

export default function DealerEngineerMapping() {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [servicePartnerOptions, setServicePartnerOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );
  const [stateOptions, setStateOptions] = useState<TDropdownOptions[]>([]);
  const [primaryEngineer, setPrimaryEngineer] = useState<TDropdownOptions>();
  const [servicePartner, setServicePartner] = useState<number | null>();
  const [secondaryEngineer, setSecondaryEngineer] =
    useState<TDropdownOptions>();
  const [serviceEscalation, setServiceEscalation] =
    useState<TDropdownOptions>();
  const [serviceCordinator, setServiceCordinator] =
    useState<TDropdownOptions>();
  const { post } = useFetch();
  const { showToast, onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isApplyingConf, setApplyConf] = useState(false);
  const [data, setData] = useState<TDealer[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [filterParams, setFilterParams] = useState<TFilterModel>({
    pageSize: -1,
    currentPage: 1,
    filterRowsCount: 0,
    totalRows: 0,
    searchText: "",
    orderType: "",
    orderBy: "",
  });
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);
  type DataIndex = keyof TDealer;
  const context: any = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser>(context?.user);
  const [searchText, setSearchText] = useState("");
  const [globalsearchText, setGlobalSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [activeView, setActiveView] = useState<"mapped" | "unmapped">("mapped");

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<TDealer> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Filter by keyword"
          prefix={
            <SearchOutlined
              style={{ color: "rgba(0,0,0,.45)", marginRight: 4 }}
            />
          } // Adds and spaces the search icon
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: "flex",
            paddingLeft: "12px", // Ensure spacing for both icon and text
          }}
        />
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          {/* RESET FILTERS */}
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            type="link"
            size="small"
            style={{ width: 45 }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            // icon={<SearchOutlined />}
            size="small"
            style={{ width: 50 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ color: filtered ? "var(--bs-primary)" : undefined }}
      />
    ),
    onFilter: (value, record) =>
      (record[dataIndex] ?? "")
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "var(--bs-primary)",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const commonSorter = (a: any, b: any, key: string) => {
    const valueA = a[key] as string;
    const valueB = b[key] as string;
    return valueA.localeCompare(valueB);
  };

  const initialColumns: TableColumnsType<TDealer> = [
    {
      title: "Dealer Name",
      dataIndex: "dealerName",
      key: "dealerName",
      ...getColumnSearchProps("dealerName"),
      width: 200,
      filterSearch: true,
      sorter: (a: any, b: any) => commonSorter(a, b, "dealerName"),
    },
    {
      title: "Depot Code",
      dataIndex: "depotCode",
      key: "depotCode",
      ...getColumnSearchProps("depotCode"),
      width: 120,
      sorter: (a: any, b: any) => commonSorter(a, b, "depotCode"),
    },
    {
      title: "Dealer Code",
      dataIndex: "dealerCode" as string,
      key: "dealerCode",
      ...getColumnSearchProps("dealerCode"),
      width: 120,
      sorter: (a: any, b: any) => commonSorter(a, b, "dealerCode"),
    },
    {
      title: "Dealer Address",
      dataIndex: "address",
      key: "address",
      ...getColumnSearchProps("address"),
      width: 400,
      sorter: (a: any, b: any) => commonSorter(a, b, "address"),
    },
    {
      title: "City",
      dataIndex: "districtName",
      key: "districtName",
      width: 120,
      filterSearch: true,
      sorter: (a: TDealer, b: TDealer) =>
        (a.districtName ?? "").localeCompare(b.districtName ?? ""),
      onFilter: (value, record) =>
        (record.districtName as string)?.toString().startsWith(value as string),
    },
    {
      title: "Pin Code",
      dataIndex: "zipcode",
      key: "zipcode",
      ...getColumnSearchProps("zipcode"),
      width: 100,
      sorter: (a: any, b: any) => commonSorter(a, b, "zipcode"),
    },
    {
      title: "State",
      dataIndex: "stateName",
      key: "stateName",
      width: 130,
      filterSearch: true,
      sorter: (a: TDealer, b: TDealer) =>
        (a.stateName ?? "").localeCompare(b.stateName ?? ""),
      onFilter: (value, record) =>
        (record.stateName as string)?.toString().startsWith(value as string),
    },
    {
      title: "Service Partner",
      dataIndex: "servicePartnerName",
      key: "servicePartnerName",
      width: 130,
      filterSearch: true,
      sorter: (a: TDealer, b: TDealer) =>
        (a.servicePartnerName ?? "").localeCompare(b.servicePartnerName ?? ""),
      onFilter: (value, record) =>
        (record.servicePartnerName as string)
          ?.toString()
          .startsWith(value as string),
    },
    {
      title: "Primary Engineer",
      dataIndex: "primaryEngineerName",
      key: "primaryEngineerName",
      width: 120,
      filterSearch: true,
      sorter: (a: TDealer, b: TDealer) =>
        (a.mappedDetails?.primaryEngineerName ?? "").localeCompare(
          b.mappedDetails?.primaryEngineerName ?? ""
        ),
      onFilter: (value: any, record: TDealer) => {
        return record.mappedDetails?.primaryEngineerName
          ?.toString()
          .startsWith(value as string) as any;
      },
      render: (val: string, dealer: TDealer) => (
        <>{dealer?.mappedDetails?.primaryEngineerName}</>
      ),
    },
    {
      title: "Secondary Engineer",
      dataIndex: "secondaryEngineerName",
      key: "secondaryEngineerName",
      width: 120,
      filterSearch: true,
      sorter: (a: TDealer, b: TDealer) =>
        (a.mappedDetails?.secondaryEngineerName ?? "").localeCompare(
          b.mappedDetails?.secondaryEngineerName ?? ""
        ),
      onFilter: (value: any, record: TDealer) => {
        return record.mappedDetails?.secondaryEngineerName
          ?.toString()
          .startsWith(value as string) as any;
      },
      render: (val: string, dealer: TDealer) => (
        <>{dealer?.mappedDetails?.secondaryEngineerName}</>
      ),
    },
    {
      title: "Service Escalation",
      dataIndex: "serviceEscalationName",
      key: "serviceEscalationName",
      width: 120,
      filterSearch: true,
      sorter: (a: TDealer, b: TDealer) =>
        (a.mappedDetails?.serviceEscalationName ?? "").localeCompare(
          b.mappedDetails?.serviceEscalationName ?? ""
        ),
      onFilter: (value: any, record: TDealer) =>
        record.mappedDetails?.serviceEscalationName
          ?.toString()
          .startsWith(value as string) as any,
      render: (val: string, dealer: TDealer) => (
        <>{dealer?.mappedDetails?.serviceEscalationName}</>
      ),
    },
    {
      title: "Service Cordinator",
      dataIndex: "serviceCordinatorName",
      key: "serviceCordinatorName",
      width: 120,
      filterSearch: true,
      sorter: (a: TDealer, b: TDealer) =>
        (a.mappedDetails?.serviceCordinatorName ?? "").localeCompare(
          b.mappedDetails?.serviceCordinatorName ?? ""
        ),
      onFilter: (value: any, record: TDealer) =>
        record.mappedDetails?.serviceCordinatorName
          ?.toString()
          .startsWith(value as string) as any,
      render: (val: string, dealer: TDealer) => (
        <>{dealer?.mappedDetails?.serviceCordinatorName}</>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);

  useEffect(() => {
    setActiveUser(context?.user);
    if (context?.user?.stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER) {
      setServicePartner(context?.user?.orgId);
    }
  }, [context?.user]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  useEffect(() => {
    fetchCustomers();
    fetchStates();
  }, []);

  useEffect(() => {
    // Clone the initial columns to avoid mutating the original array
    let updatedColumns = [...initialColumns];

    // Columns to be conditionally removed/added
    const conditionalColumns = [
      {
        title: "Primary Engineer",
        dataIndex: "primaryEngineerName",
        key: "primaryEngineerName",
        width: "25%",
        filterSearch: true,
        sorter: (a: TDealer, b: TDealer) =>
          (a.mappedDetails?.primaryEngineerName ?? "").localeCompare(
            b.mappedDetails?.primaryEngineerName ?? ""
          ),
        onFilter: (value: string | number | boolean, record: TDealer) =>
          record.mappedDetails?.primaryEngineerName?.includes(value as string),
        render: (val: string, dealer: TDealer) => (
          <>{dealer?.mappedDetails?.primaryEngineerName}</>
        ),
      },
      {
        title: "Secondary Engineer",
        dataIndex: "secondaryEngineerName",
        key: "secondaryEngineerName",
        width: "15%",
        filterSearch: true,
        sorter: (a: TDealer, b: TDealer) =>
          (a.mappedDetails?.secondaryEngineerName ?? "").localeCompare(
            b.mappedDetails?.secondaryEngineerName ?? ""
          ),
        onFilter: (value: string | number | boolean, record: TDealer) =>
          record.mappedDetails?.secondaryEngineerName?.includes(
            value as string
          ),
        render: (val: string, dealer: TDealer) => (
          <>{dealer?.mappedDetails?.secondaryEngineerName}</>
        ),
      },
      {
        title: "Service Escalation",
        dataIndex: "serviceEscalationName",
        key: "serviceEscalationName",
        width: "15%",
        filterSearch: true,
        sorter: (a: TDealer, b: TDealer) =>
          (a.mappedDetails?.serviceEscalationName ?? "").localeCompare(
            b.mappedDetails?.serviceEscalationName ?? ""
          ),
        onFilter: (value: string | number | boolean, record: TDealer) =>
          record.mappedDetails?.serviceEscalationName?.includes(
            value as string
          ),
        render: (val: string, dealer: TDealer) => (
          <>{dealer?.mappedDetails?.serviceEscalationName}</>
        ),
      },
      {
        title: "Service Cordinator",
        dataIndex: "serviceCordinatorName",
        key: "serviceCordinatorName",
        width: "15%",
        filterSearch: true,
        sorter: (a: TDealer, b: TDealer) =>
          (a.mappedDetails?.serviceCordinatorName ?? "").localeCompare(
            b.mappedDetails?.serviceCordinatorName ?? ""
          ),
        onFilter: (value: string | number | boolean, record: TDealer) =>
          record.mappedDetails?.serviceCordinatorName?.includes(
            value as string
          ),
        render: (val: string, dealer: TDealer) => (
          <>{dealer?.mappedDetails?.serviceCordinatorName}</>
        ),
      },
    ];

    // Handle conditional columns based on activeView
    if (activeView === "unmapped") {
      // Remove conditional columns
      const keysToRemove = conditionalColumns.map((col) => col.key);
      updatedColumns = updatedColumns.filter(
        (column) => !keysToRemove.includes(column.key as string)
      );

      // Add "Organization" column
      updatedColumns.splice(4, 0);
    } else if (activeView === "mapped") {
      // Add back conditional columns
      setColumns(initialColumns);
      setDraftColumns(initialColumns);
    }

    // Update the state with modified columns
    setColumns(updatedColumns);
    setDraftColumns(updatedColumns);
  }, [activeView]);

  useEffect(() => {
    filterParams?.customerId && fetchMappedPartners();
  }, [filterParams?.customerId]);

  const fetchMappedPartners = async () => {
    try {
      const payload = {
        data: {
          customerId: filterParams?.customerId,
          pageSize: -1,
          currentPage: 1,
        },
      };

      const response: TResponseModel = await post(
        GetCustMappedPartnerList,
        payload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          setServicePartnerOptions(Utils.mapDropdownOptions(data));
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const fetchCustomers = async () => {
    try {
      const payload = {
        data: {
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(GetCustomerList, payload);
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const mappedOption = Utils.mapDropdownOptions(data);
          setCustomerOptions(mappedOption);
          setFilterParams((prev) => ({
            ...prev,
            customerId: mappedOption?.[0]?.value,
          }));
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const fetchStates = async () => {
    try {
      const payload = {
        data: {
          pageSize: -1,
          countryId: 1,
        },
      };

      const response: TResponseModel = await post(GetStateList, payload);
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const mappedOption = Utils.mapDropdownOptions(data);
          setStateOptions(mappedOption);
          setFilterParams((prev) => ({
            ...prev,
            stateId: mappedOption?.[0]?.value,
          }));
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const fetchRecordsInChunks = async () => {
    const chunkSize = context?.chunkSize; // Fixed size for the first chunk
    setData([]);
    handleResetAll();
    setIsLoading(true);

    try {
      // Step 1: Fetch the total number of records
      const totalRecords = await fetchData(1, 1, true);
      let validSecondChunk: any[] = [];
      // Step 2: Fetch the first chunk
      const resp = await fetchData(1, chunkSize);
      const validFirstChunk = Array.isArray((resp as TResponseModel)?.data)
        ? (resp as TResponseModel)?.data
        : []; // Ensure valid data

      setData(validFirstChunk); // Set data to display the first chunk immediately
      updateColumnFilters((resp as TResponseModel).data);
      // Step 3: Determine the remaining records
      const remainingRecords =
        (totalRecords as number) > chunkSize
          ? (totalRecords as number) - chunkSize
          : 0;

      // Step 4: Fetch the remaining records
      if (remainingRecords > 0) {
        const response = await fetchData(2, remainingRecords);
        validSecondChunk = Array.isArray((response as TResponseModel)?.data)
          ? (response as TResponseModel)?.data
          : []; // Ensure valid data

        // Combine the data from both chunks
        updateColumnFilters([...validFirstChunk, ...validSecondChunk]);
        setData((prevData) => [...prevData, ...validSecondChunk]);
      }
      setFilterParams((prev) => ({
        ...prev,
        totalRows: [...validFirstChunk, ...validSecondChunk].length || 0,
      }));
      // setFilterParams({
      //   ...filterParams,
      //   totalRows: [...validFirstChunk, ...validSecondChunk].length || 0,
      // });
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async (
    offset: number,
    chunkSize: number,
    needTotalRows: boolean = false
  ) => {
    if (filterParams.customerId && filterParams.stateId) {
      try {
        const payload = {
          data: {
            currentPage: offset,
            pageSize: chunkSize,
            isChunkAPICall: true,
            dealerViewId: eDEALER_VIEW_TYPE.DEALERPARTNERMAPPING,
            isTotalCountRequest: needTotalRows ? 1 : 0,
            mappedDealerViewId:
              activeView === "mapped"
                ? eMAPPED_DEALER_VIEW.MAPPEDDEALER
                : eMAPPED_DEALER_VIEW.UNMAPPEDDEALER,
            customerId: filterParams.customerId,
            stateId: filterParams.stateId,
          },
        };

        const response: TResponseModel = await post(
          GetDealerPartnerMappingList,
          payload
        );
        const { data, dataResponse, filterModel } = response;
        const { description, returnCode } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          return needTotalRows ? filterModel?.totalRows : response;
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        // setIsLoading(false);
      }
    }
  };

  const updateColumnFilters = (data: TDealer[]) => {
    const stateNameFilters = Array.from(
      new Set(data.map((item: TDealer) => item.stateName))
    ).map((stateName) => ({ text: stateName, value: stateName }));
    const servicePartners = Array.from(
      new Set(data.map((item: TDealer) => item.servicePartnerName))
    ).map((stateName) => ({ text: stateName, value: stateName }));
    const districtNameFilters = Array.from(
      new Set(data.map((item: TDealer) => item.districtName))
    ).map((districtName) => ({
      text: districtName,
      value: districtName,
    }));
    const primaryEngineerNameFilters = Array.from(
      new Set(
        data.map((item: TDealer) => item.mappedDetails?.primaryEngineerName)
      )
    ).map((primaryEngineerName) => ({
      text: primaryEngineerName,
      value: primaryEngineerName,
    }));
    const secondaryEngineerNameFilters = Array.from(
      new Set(
        data.map((item: TDealer) => item.mappedDetails?.secondaryEngineerName)
      )
    ).map((secondaryEngineerName) => ({
      text: secondaryEngineerName,
      value: secondaryEngineerName,
    }));
    const serviceEscalationNameFilters = Array.from(
      new Set(
        data.map((item: TDealer) => item.mappedDetails?.serviceEscalationName)
      )
    ).map((serviceEscalationName) => ({
      text: serviceEscalationName,
      value: serviceEscalationName,
    }));
    const serviceCordinatorNameFilters = Array.from(
      new Set(
        data.map((item: TDealer) => item.mappedDetails?.serviceCordinatorName)
      )
    ).map((serviceCordinatorName) => ({
      text: serviceCordinatorName,
      value: serviceCordinatorName,
    }));

    setColumns(
      (prevColumns) =>
        prevColumns.map((col) => {
          if ((col.key as string) === "stateName") {
            return { ...col, filters: stateNameFilters };
          }
          if (col.key === "districtName") {
            return { ...col, filters: districtNameFilters };
          }
          if (col.key === "primaryEngineerName") {
            return { ...col, filters: primaryEngineerNameFilters };
          }
          if (col.key === "secondaryEngineerName") {
            return { ...col, filters: secondaryEngineerNameFilters };
          }
          if (col.key === "serviceEscalationName") {
            return { ...col, filters: serviceEscalationNameFilters };
          }
          if (col.key === "serviceCordinatorName") {
            return { ...col, filters: serviceCordinatorNameFilters };
          }
          if (col.key === "servicePartnerName") {
            return { ...col, filters: servicePartners };
          }
          return col;
        }) as any
    );
  };

  const onApplyConfiguration = async () => {
    try {
      // setIsLoading(true);
      setApplyConf(true);
      const updatedDealerList = (() => {
        const selectedSet = new Set(selectedRows); // Use Set for faster lookups

        return data
          .filter((dealer: TDealer) => selectedSet.has(dealer?.id as number)) // Filter for matching dealers
          .map((dealer: TDealer) => ({
            dealerId: dealer.id,
            dealerCode: dealer.dealerCode,
          }));
      })();
      const payload = {
        data: {
          dealerIdList: updatedDealerList,
          servicePartnerId: servicePartner,
          customerId: filterParams?.customerId,
          primaryEngineerId: primaryEngineer?.value,
          secondaryEngineerId: secondaryEngineer?.value,
          serviceEscalationId: serviceEscalation?.value,
          serviceCordinatorId: serviceCordinator?.value,
        },
      };

      const response: TResponseModel = await post(
        AddEditDealerPartnerMapping,
        payload
      );
      const { dataResponse } = response;
      const { description, returnCode } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        setPrimaryEngineer({ value: null, label: "" });
        setSecondaryEngineer({ value: null, label: "" });
        setServiceEscalation({ value: null, label: "" });
        setServiceCordinator({ value: null, label: "" });
        setSelectedRows([]);
        setShowModal(false);
        // setIsLoading(false);
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        fetchRecordsInChunks();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      // setIsLoading(false);
      setApplyConf(false);
    }
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((val) =>
      val.toString().toLowerCase().includes(globalsearchText.toLowerCase())
    )
  );

  const visibleColumns = columns.filter((col) => !col.hidden);

  const handleRowSelection = (id: number, isSelected: boolean) => {
    setSelectedRows((prevSelected) =>
      isSelected
        ? [...prevSelected, id]
        : prevSelected.filter((selectedId) => selectedId !== id)
    );
  };

  useEffect(() => {
    fetchRecordsInChunks();
  }, [activeView, filterParams.customerId, filterParams.stateId]);

  const handleSelectAll = (isSelected: boolean) => {
    setSelectedRows(
      isSelected ? data.map((dealer) => dealer.id as number) : []
    );
  };

  useEffect(() => {
    const newUrl = new URL(window.location.href);
    const customerId = newUrl.searchParams.get("customerId");
    const stateId = newUrl.searchParams.get("stateId");

    console.log("querparams", customerId, stateId);

    if (customerId) {
      setFilterParams((prev) => ({
        ...prev,
        customerId: parseInt(customerId, 10),
      }));
    } else if (customerOptions.length > 0 && !filterParams.customerId) {
      const firstOption = customerOptions[0];
      setFilterParams((prev) => ({
        ...prev,
        customerId: firstOption.value as number,
      }));
    }
    if (stateId) {
      setFilterParams((prev) => ({
        ...prev,
        stateId: parseInt(stateId, 10),
      }));
    } else if (stateOptions.length > 0 && !filterParams.stateId) {
      const firstOption = stateOptions[0];
      setFilterParams((prev) => ({
        ...prev,
        stateId: firstOption.value as number,
      }));
    }
  }, [customerOptions, stateOptions]);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  exportHeaders.unshift({ label: "Customer", key: "customerName" });

  const exportData = data.map((user) => ({
    ...user,
  }));

  return (
    <div className="pageBody">
      <PageHeader
        title={
          isLoading ? (
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Spin size="small" /> Total Dealers
            </span>
          ) : (
            `${filterParams?.totalRows} Total Dealers`
          )
        }
        size="large"
        className={styles.pageHeaderStyles}
      >
        <div className={styles.headerParentWrap}>
          <div style={{ display: "flex" }}>
            <Select
              disabled={isLoading}
              showSearch
              variant="borderless"
              style={{ width: "15vw" }}
              placeholder="Select customer"
              options={customerOptions}
              value={filterParams.customerId} // Set the value to the selected customerId
              onChange={(value: any) => {
                const newurl = new URL(window.location.href);
                newurl.searchParams.set("customerId", value.toString());
                window.history.pushState({}, "", newurl.toString());

                setFilterParams((prev) => ({
                  ...prev,
                  customerId: value,
                }));
                setSelectedRows([]);
              }}
              filterOption={(input, option) =>
                option?.label
                  ? option.label.toLowerCase().includes(input.toLowerCase())
                  : false
              }
            />
            <Select
              disabled={isLoading}
              showSearch
              variant="borderless"
              style={{ width: "15vw" }}
              placeholder="Select state"
              options={stateOptions}
              value={filterParams.stateId} // Set the value to the selected customerId
              onChange={(value: any) => {
                const newurl = new URL(window.location.href);
                newurl.searchParams.set("stateId", value.toString());
                window.history.pushState({}, "", newurl.toString());

                setFilterParams((prev) => ({
                  ...prev,
                  stateId: value,
                }));
                setSelectedRows([]);
              }}
              filterOption={(input, option) =>
                option?.label
                  ? option.label.toLowerCase().includes(input.toLowerCase())
                  : false
              }
            />
          </div>
        </div>
        <div className={styles.headerRightSection}>
          {/* DEALER ENGINEER MAPPING COULD ONLY BE DONE BY SERVICEPARTNER STAKEHOLDER'S USERS */}
          {Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.MAPDEALERENGG
          ) &&
          context?.user?.stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER ? (
            <Button
              type="primary"
              onClick={() => setShowModal(true)}
              disabled={selectedRows.length === 0}
            >
              Assign Engineer
            </Button>
          ) : null}

          <Input
            placeholder="Filter by keywords"
            value={globalsearchText}
            onChange={(e) => setGlobalSearchText(e.target.value)}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`${activeView === "mapped" ? "mapped" : "unmapped"}_dealers_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </div>
      </PageHeader>
      <div className={styles.dealersContainer}>
        {/* Mapped Dealers Tab */}
        <div
          className={`${styles.tab} ${
            activeView === "mapped" ? styles.activeTab : ""
          } ${isLoading ? styles.disabled : ""}`}
          onClick={() => {
            if (!isLoading) {
              setActiveView("mapped");
              setData([]);
              handleResetAll();
              setSelectedRows([]);
            }
          }}
        >
          <span
            className={
              activeView === "mapped" ? styles.activeText : styles.inactiveText
            }
          >
            Mapped Dealers
          </span>
        </div>

        {/* Unmapped Dealers Tab */}
        <div
          className={`${styles.tab} ${
            activeView === "unmapped" ? styles.activeTab : ""
          } ${isLoading ? styles.disabled : ""}`}
          onClick={() => {
            if (!isLoading) {
              setActiveView("unmapped");
              setData([]);
              handleResetAll();
              setSelectedRows([]);
            }
          }}
        >
          <span
            className={
              activeView === "unmapped"
                ? styles.activeText
                : styles.inactiveText
            }
          >
            Unmapped Dealers
          </span>
        </div>
      </div>

      <span
        style={{ margin: "0 1rem", fontSize: "12px" }}
      >{`Selected ${selectedRows?.length} from total ${data?.length}`}</span>
      <div className="tableWrap">
        <Table<TDealer>
          rowSelection={{
            columnWidth: 30,
            type: "checkbox",
            selectedRowKeys: selectedRows,
            onChange: (selectedRowKeys) => {
              setSelectedRows(selectedRowKeys as number[]);
            },
            onSelect: (record, selected) => {
              if (record.id !== undefined) {
                handleRowSelection(record.id, selected);
              }
            },
            onSelectAll: (selected) => {
              handleSelectAll(selected);
            },
          }}
          onChange={handleTableChange}
          sticky
          scroll={{
            x: "max-content", // Horizontal scrolling
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
          pagination={{
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "100", "500", "1000"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          dataSource={filteredData?.map((item: TDealer) => ({
            ...item,
            key: item.id,
          }))}
          columns={visibleColumns}
          loading={isLoading}
          // size="small"
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns as any}
          initialColumns={initialColumns as any}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
      <CustomModal
        title="Configure Engineers"
        isOpen={showModal}
        disableOk={
          isLoading ||
          isApplyingConf ||
          !(
            primaryEngineer?.value &&
            secondaryEngineer?.value &&
            serviceEscalation?.value &&
            serviceCordinator?.value
          )
        }
        closeIcon={isApplyingConf ? false : true}
        hideCancelButton={isApplyingConf ? true : false}
        okText="Apply"
        onOk={onApplyConfiguration} // Passing the custom function
        onCancel={() => {
          setPrimaryEngineer({ value: null, label: "" });
          setSecondaryEngineer({ value: null, label: "" });
          setServiceEscalation({ value: null, label: "" });
          setServiceCordinator({ value: null, label: "" });
          // setServicePartner(null);
          setShowModal(false);
        }}
      >
        {isApplyingConf ? (
          <Row
            justify="center"
            align="middle"
            style={{ height: "450px", padding: "20px", width: "100%" }}
          >
            <Spin size="large">
              <div style={{ marginTop: "4rem" }}>
                Engineer Mapping under progress...
              </div>
            </Spin>
          </Row>
        ) : (
          <MappingForm
            customerId={filterParams?.customerId as number}
            servicePartner={servicePartner ?? null}
            primaryEngineer={primaryEngineer}
            secondaryEngineer={secondaryEngineer}
            serviceEscalation={serviceEscalation}
            serviceCordinator={serviceCordinator}
            servicePartnerOptions={servicePartnerOptions}
            activeUser={activeUser}
            setPrimaryEngineer={setPrimaryEngineer}
            setSecondaryEngineer={setSecondaryEngineer}
            setServiceEscalation={setServiceEscalation}
            setServiceCordinator={setServiceCordinator}
            setServicePartner={setServicePartner}
          />
        )}
      </CustomModal>
    </div>
  );
}
